import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../../api/axios";
import {
  LayoutDashboard,
  Users,
  UserPlus,
  Truck,
  FileText,
  Grid,
  Tag,
  Package,
  Box,
  ArrowDownCircle,
  ArrowUpCircle,
  ClipboardList,
  Sun,
  ChevronDown,
  ChevronRight,
  MapPin,
  Menu,
  ChevronLeft,
  Archive,
} from "lucide-react";
import { getUserRole } from "../../utils/getUserRole";

function SidebarNavbar({ isMobileOpen, onMobileClose }) {
  const location = useLocation();
  const [role, setRole] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [purchaseOrderCount, setPurchaseOrderCount] = useState(0);
  const [openMenus, setOpenMenus] = useState({
    insumos: false,
    facturas: false,
    activos: false,
    Solicitudes: true,
  });

  useEffect(() => {
    const userRole = getUserRole();
    const canViewRequests = ["Administrador", "Solicitudes", "Insumos"].includes(userRole);

    setRole(userRole);

    if (canViewRequests) loadPendingRequests();
    loadPurchaseOrderCount(userRole);

    const interval = setInterval(() => {
      if (canViewRequests) loadPendingRequests();
      loadPurchaseOrderCount(userRole);
    }, 10000);

    setOpenMenus({
      insumos: userRole === "Administrador" || userRole === "Insumos",
      facturas: userRole === "Facturacion",
      activos: userRole === "Activos" || userRole === "Admin-Odontologia",
      Solicitudes: true,
    });

    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 1370) setIsCollapsed(true);
      else setIsCollapsed(false);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    onMobileClose();
  }, [location.pathname, onMobileClose]);

  const collapsed = isMobile ? false : isCollapsed;

  const toggleMenu = (menu) => {
    if (!collapsed) {
      setOpenMenus((prev) => ({ ...prev, [menu]: !prev[menu] }));
    }
  };

  const loadPendingRequests = async () => {
    try {
      const res = await api.get("/inventory-requests/pending-count");

      setPendingRequests(res.data.total);
    } catch (error) {
      console.error(error);
    }
  };

  const loadPurchaseOrderCount = async (userRole) => {
    try {
      const statuses = userRole === "Administrador"
        ? ["draft", "under_review"]
        : userRole === "Insumos"
          ? ["approved", "not_approved"]
          : [];

      if (statuses.length === 0) {
        setPurchaseOrderCount(0);
        return;
      }

      const responses = await Promise.all(
        statuses.map((status) =>
          api.get("/purchase-orders", { params: { status } }),
        ),
      );
      setPurchaseOrderCount(
        responses.reduce((total, response) => total + (response.data?.length || 0), 0),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const isActive = (path) => location.pathname === path;

  const NavLink = ({ to, icon, label, isMain = false, groupId = null }) => {
    const shouldHighlight =
      isActive(to) && (!groupId || openMenus[groupId] || collapsed);

    return (
      <Link
        to={to}
        onClick={onMobileClose}
        title={label}
        className={`flex items-center gap-3 transition-all duration-200 rounded-lg ${
          isMain ? "py-2.5 px-4" : "py-2 px-4"
        } ${
          shouldHighlight
            ? "bg-blue-600 text-white shadow-md shadow-blue-900/20 font-bold"
            : "text-gray-400 hover:bg-gray-800 hover:text-white"
        } ${collapsed ? "justify-center px-0 mx-2" : isMain ? "text-sm" : "ml-4 text-[13px]"}`}
      >
        <div className="flex-shrink-0">{icon}</div>
        {!collapsed && <span className="truncate">{label}</span>}
      </Link>
    );
  };

  const NavGroup = ({ id, label, icon, children }) => {
    const isOpen = openMenus[id];
    return (
      <div className="flex flex-col mb-1">
        <button
          onClick={() => toggleMenu(id)}
          className={`flex items-center justify-between px-4 py-2 rounded-lg text-gray-500 hover:text-white transition-colors ${
            collapsed ? "justify-center py-4" : ""
          }`}
        >
          <div className="flex items-center gap-3">
            {icon}
            {!collapsed && (
              <span className="text-xs font-bold uppercase tracking-widest">
                {label}
              </span>
            )}
          </div>
          {!collapsed &&
            (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
        </button>
        {(isOpen || collapsed) && (
          <div
            className={`flex flex-col space-y-1 ${!collapsed ? "mt-1 border-l border-gray-800 ml-2" : ""}`}
          >
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 flex h-screen w-64 flex-col overflow-x-hidden overflow-y-auto bg-[#0f172a] p-3 text-white shadow-2xl transition-transform duration-300 lg:relative lg:translate-x-0 lg:transition-[width] ${
        isMobileOpen ? "translate-x-0" : "-translate-x-full"
      } ${isCollapsed ? "lg:w-20" : "lg:w-64"}`}
    >
      <div
        className={`mb-8 mt-2 flex items-center ${collapsed ? "justify-center" : "justify-between px-2"}`}
      >
        {!collapsed && (
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold tracking-tight">MEDICARE</span>
          </div>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? "Expandir menú lateral" : "Contraer menú lateral"}
          className="hidden rounded-xl p-2 text-gray-400 hover:bg-gray-800 lg:block"
        >
          {isCollapsed ? <Menu size={22} /> : <ChevronLeft size={22} />}
        </button>
      </div>

      <nav className="flex flex-col space-y-1">
        {/* --- VISTA PARA ROL DASHBOARDS (SIMPLIFICADA) --- */}
        {role === "Dashboards" ? (
          <div className="flex flex-col space-y-2">
            {!collapsed}
            <NavLink
              to="/dashboard"
              icon={<LayoutDashboard size={20} />}
              label="Dashboard Insumos"
              isMain={true}
            />
            <NavLink
              to="/invoices/dashboard"
              icon={<FileText size={20} />}
              label="Dashboard Facturas"
              isMain={true}
            />
            <NavLink
              to="/actives/dashboard"
              icon={<Sun size={20} />}
              label="Dashboard Activos"
              isMain={true}
            />
            
          </div>
        ) : (
          /* --- VISTA PARA OTROS ROLES (CON GRUPOS) --- */
          <>
            {role === "Administrador" && (
              <div className="mb-2">
                <NavLink
                  to="/users"
                  icon={<Users size={20} />}
                  label="Usuarios"
                  isMain={true}
                />
                {!collapsed && (
                  <div className="mx-4 mt-2 border-t border-gray-800/40"></div>
                )}
              </div>
            )}

            {/* GRUPO 1: INSUMOS */}
            {(role === "Administrador" || role === "Insumos") && (
              <NavGroup id="insumos" label="Insumos" icon={<Box size={18} />}>
                <NavLink
                  to="/dashboard"
                  icon={<LayoutDashboard size={18} />}
                  label="Dashboard Insumos"
                />
                <NavLink
                  to="/kardex"
                  icon={<ClipboardList size={18} />}
                  label="Kardex"
                />
                <NavLink
                  to="/products"
                  icon={<Package size={18} />}
                  label="Insumos"
                />
                <NavLink
                  to="/clients"
                  icon={<UserPlus size={18} />}
                  label="Clientes Internos"
                />
                <NavLink
                  to="/suppliers"
                  icon={<Truck size={18} />}
                  label="Proveedores"
                  groupId="insumos"
                />
                <NavLink to="/brands" icon={<Tag size={18} />} label="Marcas" />
                <NavLink
                  to="/presentations"
                  icon={<Grid size={18} />}
                  label="Presentaciones"
                />
                <NavLink
                  to="/purchase-orders"
                  icon={<FileText size={18} />}
                  label={
                    <div className="flex items-center gap-2">
                      <span>Orden de compra</span>

                      {((role === "Administrador" || role === "Insumos") &&
                        purchaseOrderCount > 0) && (
                          <span className="bg-red-600 text-white text-xs rounded-full px-2 py-0.5 font-bold">
                            {purchaseOrderCount}
                          </span>
                        )}
                    </div>
                  }
                />
                <NavLink
                  to="/entries"
                  icon={<ArrowDownCircle size={18} />}
                  label="Entradas"
                />
                
                <NavLink
                  to="/exits"
                  icon={<ArrowUpCircle size={18} />}
                  label="Salidas"
                />
              </NavGroup>
            )}

            {/* GRUPO 2: FACTURACIÓN */}
            {(role === "Administrador" || role === "Facturacion") && (
              <NavGroup
                id="facturas"
                label="Facturas"
                icon={<FileText size={18} />}
              >
                <NavLink
                  to="/invoices/dashboard"
                  icon={<LayoutDashboard size={18} />}
                  label="Dashboard Facturas"
                  groupId="facturas"
                />
                <NavLink
                  to="/suppliers"
                  icon={<Truck size={18} />}
                  label="Proveedores"
                  groupId="facturas"
                />
                <NavLink
                  to="/invoices"
                  icon={<FileText size={18} />}
                  label="Listado Facturas"
                  groupId="facturas"
                />
              </NavGroup>
            )}

            {/* GRUPO 3: ACTIVOS */}
            {(role === "Administrador" || role === "Activos" || role === "Admin-Odontologia") && (
              <NavGroup id="activos" label="Activos" icon={<Sun size={18} />}>
                {role !== "Admin-Odontologia" && (
                  <NavLink
                    to="/actives/dashboard"
                    icon={<LayoutDashboard size={18} />}
                    label="Dashboard Activos"
                    groupId="activos"
                  />
                )}
                <NavLink
                  to="/actives"
                  icon={<Box size={18} />}
                  label="Activos"
                  groupId="activos"
                />
                {role !== "Admin-Odontologia" && (
                  <>
                    <NavLink
                      to="/actives/categories"
                      icon={<Grid size={18} />}
                      label="Categorías"
                      groupId="activos"
                    />
                    <NavLink
                      to="/actives/locations"
                      icon={<MapPin size={18} />}
                      label="Ubicaciones"
                      groupId="activos"
                    />
                  </>
                )}
              </NavGroup>
            )}

            {/* GRUPO 4: PEDIDOS */}
            {(role === "Administrador" || role === "Solicitudes" || role === "Insumos") && (
              <NavGroup
                id="Solicitudes"
                label="Solicitudes"
                icon={<ClipboardList size={18} />}
              >
                <NavLink
                  to="/requests"
                  icon={<Box size={18} />}
                  label={
                    <div className="flex items-center gap-2">
                      <span>Solicitudes</span>

                      {(role === "Administrador" || role === "Insumos") &&
                        pendingRequests > 0 && (
                          <span className="bg-red-600 text-white text-xs rounded-full px-2 py-0.5 font-bold">
                            {pendingRequests}
                          </span>
                        )}
                    </div>
                  }
                  groupId="Solicitudes"
                />
              </NavGroup>
            )}

            {/* GRUPO 5: MI BODEGA */}
            {(role === "Administrador" || role === "Solicitudes") && (
              <NavGroup
                id="mi-bodega"
                label="Mi Bodega"
                icon={<Package size={18} />}
              >
                <NavLink
                  to="/my-inventory"
                  icon={<Archive size={18} />}
                  label="Inventario"
                  groupId="mi-inventario"
                />
              </NavGroup>
            )}
          </>
        )}
      </nav>

      {!collapsed && (
        <div className="mt-auto p-4 text-center">
          <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">
            v1.1.0
          </p>
        </div>
      )}
    </aside>
  );
}

export default SidebarNavbar;
